import { useCallback } from "react";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";

import { setUserHeader } from "../../services/granularAxios";
import { SimpleSetValue } from "../../types";

export type UserId = string;

// Do NOT export atoms.
// This allows us to limit usage of these to only the hooks we export below.

const userIdAtom = atom<UserId | undefined>({
  key: "[@fabric/core] userIdAtom",
  default: undefined,
});

export const userIdAccessError = new Error(
  "Attempted to access userId but it is not available",
);

// ##################
// ## useSetUserId ##
// ##################

export type ResultOfUseSetUserId = SimpleSetValue<UserId | undefined>;
export type UseSetUserId = () => ResultOfUseSetUserId;

/**
 * Call the returned value of this hook to set the user id to the given user id.
 *
 * This does no network requests. This is not a substitue
 * for `useUpdateUser` or the authentication logic.
 */
export const useSetUserId: UseSetUserId = () => {
  const setUserId = useSetRecoilState(userIdAtom);
  return useCallback(
    (userId: UserId | undefined) => {
      setUserHeader({ granularId: userId });
      setUserId(userId);
    },
    [setUserId],
  );
};

// ####################
// ## useMaybeUserId ##
// ####################

export type ResultOfUseMaybeUserId = UserId | undefined;
export type UseMaybeUserId = () => ResultOfUseMaybeUserId;

/**
 * The returned value of this hook will contain
 * the current user id, which may be undefined.
 *
 * Use this over the user id from `useUser` or `useAuthentication` as this hook
 * will not trigger a re-render for you unless the user id and only the user id changes.
 */
export const useMaybeUserId: UseMaybeUserId = () => {
  return useRecoilValue(userIdAtom);
};

// ###############
// ## useUserId ##
// ###############

export type ResultOfUseUserId = UserId;
export type UseUserId = () => ResultOfUseUserId;

/**
 * The returned value of this hook will contain the current user id.
 *
 * This hook is guaranteed to contain the user id or will throw.
 *
 * Use this over the user id from `useUser` or `useAuthentication` as this hook
 * will not trigger a re-render for you unless the user id and only the user id changes.
 */
export const useUserId: UseUserId = () => {
  const userId = useMaybeUserId();
  if (userId === undefined) {
    throw userIdAccessError;
  }
  return userId;
};
